<template>
    <b-modal
        :id="modalId"
        ref="modal"
        title="Contexto da Sessão"
        size="lg"
        scrollable
        hide-footer
    >
        <div v-if="carregando" class="text-center py-4">
            <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
            <p class="mt-2 text-muted">Carregando contexto...</p>
        </div>
        <div v-else-if="dados">
            <!-- Info da Sessão -->
            <div class="context-section mb-3">
                <h6><i class="fa fa-info-circle mr-1"></i> Sessão</h6>
                <table class="table table-sm table-bordered">
                    <tbody>
                        <tr>
                            <td class="font-weight-bold" style="width:160px">ID</td>
                            <td><code>{{ dados.sessionId }}</code></td>
                        </tr>
                        <tr v-if="dados.createdAt">
                            <td class="font-weight-bold">Criada em</td>
                            <td>{{ formatarData(dados.createdAt) }}</td>
                        </tr>
                        <tr>
                            <td class="font-weight-bold">Mensagens</td>
                            <td>{{ dados.messagesCount || 0 }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Contexto Atual -->
            <div class="context-section mb-3" v-if="dados.context">
                <h6><i class="fa fa-brain mr-1"></i> Contexto Atual</h6>
                <table class="table table-sm table-bordered">
                    <tbody>
                        <tr v-if="dados.context.lastCategory">
                            <td class="font-weight-bold" style="width:160px">Última Categoria</td>
                            <td><span class="badge badge-info">{{ dados.context.lastCategory }}</span></td>
                        </tr>
                        <tr v-if="dados.context.lastTool">
                            <td class="font-weight-bold">Última Ferramenta</td>
                            <td><span class="badge badge-secondary">{{ dados.context.lastTool }}</span></td>
                        </tr>
                        <tr>
                            <td class="font-weight-bold">Pendência</td>
                            <td>
                                <span v-if="dados.context.hasPending" class="badge badge-warning">Sim</span>
                                <span v-else class="badge badge-light">Nenhuma</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Zep Context (Memória Inteligente) -->
            <div class="context-section mb-3" v-if="dados.zepEnabled">
                <h6>
                    <i class="fa fa-project-diagram mr-1"></i> Zep - Contexto Inteligente
                    <span v-if="dados.zepContext" class="badge badge-success ml-1">Ativo</span>
                    <span v-else class="badge badge-secondary ml-1">Sem dados</span>
                </h6>
                <div v-if="dados.zepContext" class="zep-context-block p-2 rounded" style="background: #f0f4f8; border: 1px solid #d0d9e3; max-height: 300px; overflow-y: auto;">
                    <pre style="font-size: 0.8rem; white-space: pre-wrap; word-break: break-word; margin: 0; font-family: 'Segoe UI', sans-serif;">{{ dados.zepContext }}</pre>
                </div>
                <small v-else class="text-muted">O Zep ainda não gerou contexto para esta sessão. Ele precisa de algumas mensagens para extrair entidades e fatos.</small>
            </div>

            <!-- Últimas Mensagens (snapshot) -->
            <div class="context-section" v-if="dados.messages && dados.messages.length > 0">
                <h6>
                    <i class="fa fa-history mr-1"></i> 
                    Mensagens ({{ dados.messages.length }} de {{ dados.messagesCount || dados.messages.length }})
                </h6>

                <!-- Botão carregar mais -->
                <div v-if="podeCarregarMais" class="text-center mb-2">
                    <button 
                        class="btn btn-sm btn-outline-primary"
                        @click="carregarMais"
                        :disabled="carregandoMais"
                    >
                        <i :class="carregandoMais ? 'fa fa-spinner fa-spin' : 'fa fa-arrow-up'" class="mr-1"></i>
                        {{ carregandoMais ? 'Carregando...' : 'Carregar mais mensagens' }}
                    </button>
                </div>

                <div class="context-messages-list" style="max-height: 400px; overflow-y: auto;">
                    <div 
                        v-for="(msg, i) in dados.messages" 
                        :key="i"
                        class="context-message p-2 mb-1 rounded"
                        :class="msg.role === 'user' ? 'bg-light' : 'bg-white border'"
                    >
                        <small class="font-weight-bold" :class="msg.role === 'user' ? 'text-primary' : 'text-success'">
                            <i :class="msg.role === 'user' ? 'fa fa-user' : 'fa fa-robot'"></i>
                            {{ msg.role === 'user' ? 'Você' : 'Assistente' }}
                        </small>
                        <div class="mt-1" style="font-size: 0.85rem; white-space: pre-wrap; word-break: break-word;">{{ truncar(msg.content) }}</div>
                        <small class="text-muted" v-if="msg.created_at || msg.timestamp">{{ formatarData(msg.created_at || msg.timestamp) }}</small>
                    </div>
                </div>
            </div>

            <!-- Sem contexto -->
            <div v-if="!dados.context && (!dados.messages || dados.messages.length === 0)" class="text-center py-3 text-muted">
                <i class="fa fa-inbox fa-2x mb-2"></i>
                <p>Nenhum contexto disponível nesta sessão.</p>
            </div>
        </div>
        <div v-else class="text-center py-3 text-muted">
            <p>Não foi possível carregar o contexto.</p>
        </div>
    </b-modal>
</template>

<script>
const LIMIT_INICIAL = 20
const LIMIT_INCREMENTO = 50

export default {
    name: 'ModalContextoSessao',

    props: {
        modalId: {
            type: String,
            default: 'modal-contexto-sessao'
        }
    },

    data() {
        return {
            dados: null,
            carregando: false,
            carregandoMais: false,
            limiteAtual: LIMIT_INICIAL,
            sessaoTokenAtual: null
        }
    },

    computed: {
        podeCarregarMais() {
            if (!this.dados || !this.dados.messages) return false
            return this.dados.messages.length < (this.dados.messagesCount || 0)
        }
    },

    methods: {
        async abrir(sessaoToken) {
            if (!sessaoToken) return
            this.sessaoTokenAtual = sessaoToken
            this.limiteAtual = LIMIT_INICIAL
            this.dados = null
            this.carregando = true
            this.$bvModal.show(this.modalId)
            await this.buscarContexto()
        },

        async buscarContexto() {
            try {
                const response = await this.$api.cPost('/ia/agent_context', {
                    session_token: this.sessaoTokenAtual,
                    limit: this.limiteAtual
                })
                const data = response.dados || response.data || response
                if (data) {
                    this.dados = data
                }
            } catch (e) {
                console.error('Erro ao carregar contexto:', e)
                this.dados = null
            } finally {
                this.carregando = false
                this.carregandoMais = false
            }
        },

        async carregarMais() {
            this.carregandoMais = true
            this.limiteAtual += LIMIT_INCREMENTO
            await this.buscarContexto()
        },

        fechar() {
            this.$bvModal.hide(this.modalId)
        },

        formatarData(data) {
            if (!data) return ''
            try {
                const d = new Date(data)
                return d.toLocaleString('pt-BR')
            } catch (e) {
                return data
            }
        },

        truncar(content, max = 500) {
            const text = content || ''
            return text.length > max ? text.substring(0, max) + '...' : text
        }
    }
}
</script>
